/**
 * One source of truth for status pills across every agent (Hermes, Daedalus,
 * Prometheus, Argus). A small colored dot + label, so a status reads the same
 * wherever it appears. Unknown statuses fall back to a neutral pill with the raw
 * value humanized, so nothing ever renders blank.
 */
const STATUS_META: Record<string, { label: string; dot: string; pill: string }> = {
  // lifecycle / shared
  pending: { label: 'Pending', dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600' },
  drafting: { label: 'Drafting', dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600' },
  analyzing: { label: 'Analyzing', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  reporting: { label: 'Reporting', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  ingesting_context: { label: 'Loading context', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  generating: { label: 'Generating', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  reviewing: { label: 'Review', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700' },
  approved: { label: 'Approved', dot: 'bg-green-500', pill: 'bg-green-50 text-green-700' },
  creating_tickets: { label: 'Creating tickets', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  // daedalus
  cloning: { label: 'Cloning', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  implementing: { label: 'Implementing', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  waiting_for_input: { label: 'Waiting for input', dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-700' },
  pushing: { label: 'Pushing', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  pr_created: { label: 'PR created', dot: 'bg-primary', pill: 'bg-primary-light text-primary' },
  in_review: { label: 'In review', dot: 'bg-yellow-500', pill: 'bg-yellow-50 text-yellow-700' },
  revising: { label: 'Revising', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  // prometheus / argus
  running: { label: 'Running', dot: 'bg-blue-500 animate-pulse', pill: 'bg-blue-50 text-blue-600' },
  waiting_approval: { label: 'Awaiting approval', dot: 'bg-amber-500', pill: 'bg-amber-100 text-amber-700' },
  planned: { label: 'Planned', dot: 'bg-amber-500', pill: 'bg-amber-50 text-amber-700' },
  passed: { label: 'Passed', dot: 'bg-green-500', pill: 'bg-green-50 text-green-700' },
  declined: { label: 'Declined', dot: 'bg-gray-400', pill: 'bg-gray-100 text-gray-600' },
  // terminal
  completed: { label: 'Completed', dot: 'bg-green-500', pill: 'bg-success-light text-success' },
  failed: { label: 'Failed', dot: 'bg-red-500', pill: 'bg-danger-light text-danger' },
};

export function StatusBadge({
  status,
  label,
  className = '',
}: {
  status: string;
  /** Override the default label when a context needs a more specific word for the same status. */
  label?: string;
  className?: string;
}) {
  const meta = STATUS_META[status] ?? {
    label: status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    dot: 'bg-gray-400',
    pill: 'bg-gray-100 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-0.5 rounded-full ${meta.pill} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {label ?? meta.label}
    </span>
  );
}
